import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Cloud, LogOut, AlertTriangle, Trash2, Info, UserRound, History, Menu, X, Bell, ExternalLink, ChevronRight } from 'lucide-react';
import api, { API_BASE } from './services/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Deploy from './pages/Deploy';
import Activity from './pages/Activity';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminLogin from './pages/SuperAdminLogin';
import Settings from './pages/Settings';
import Backups from './pages/Backups';
import Admin from './pages/Admin';
import Navigation from './components/Navigation';
import Landing from './pages/Landing';
import Profile from './pages/Profile';
import Billing from './pages/Billing';
import Plans from './pages/Plans';
import Support from './pages/Support';
import Notifications from './pages/Notifications';
import Integrations from './pages/Integrations';
import Onboarding from './pages/Onboarding';
import ComposeWorkspaceModal from './components/ComposeWorkspaceModal';
import FirstLoginPasswordModal from './components/FirstLoginPasswordModal';
import appStyles from './App.module.css';

function AppContent() {
  const navigate = useNavigate();

  // Wizard "deploying" progress cannot survive a hard reload. Land on configure
  // (session draft) or the deploy home instead of a blank page.
  const stabilizePersistedView = (view) => {
    const v = String(view || '').trim();
    if (!v) return 'dashboard';
    if (v.includes('-deploying')) {
      if (v.startsWith('deploy-repo')) return 'deploy-repo-configure';
      if (v.startsWith('deploy-hub')) return 'deploy-hub';
      if (v.startsWith('deploy-')) return 'deploy';
      return 'deploy';
    }
    return v;
  };

  const getInitialView = () => {
    if (!api.user) return 'landing';
    return stabilizePersistedView(localStorage.getItem('lastView') || 'dashboard');
  };
  const [currentView, setCurrentView] = useState(getInitialView());

  useEffect(() => {
    if (api.user && currentView !== 'landing' && currentView !== 'login') {
      localStorage.setItem('lastView', stabilizePersistedView(currentView));
    }
  }, [currentView, api.user]);
  const [viewKeys, setViewKeys] = useState({
    dashboard: 0,
    deploy: 0,
    'auto-deploy': 0,
    activity: 0,
    admin: 0,
    settings: 0,
    backups: 0,
    notifications: 0,
    stats: 0
  });

  const handleViewChange = (viewId) => {
    setViewKeys(prev => ({
      ...prev,
      [viewId]: (prev[viewId] || 0) + 1
    }));
    setCurrentView(viewId);
  };

  const [user, setUser] = useState(api.user);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ visible: false, title: '', message: '', onConfirm: null, type: 'info', confirmText: 'Confirm' });

  const showConfirm = (title, message, onConfirm, type = 'info', confirmText = 'Confirm') => {
    setConfirmDialog({ visible: true, title, message, onConfirm, type, confirmText });
  };
  const [statusVisible, setStatusVisible] = useState(false);
  const statusTimerRef = useRef(null);
  const statusFadeTimerRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Global Workspace State (persists across tabs)
  const [globalWorkspace, setGlobalWorkspace] = useState(null);
  const [globalWorkspaceMinimized, setGlobalWorkspaceMinimized] = useState(false);

  const [loginTime, setLoginTime] = useState(new Date().toLocaleTimeString());
  const [showLogin, setShowLogin] = useState(false);
  const [initialSignUpMode, setInitialSignUpMode] = useState(false);
  const [authNotice, setAuthNotice] = useState(null);

  useEffect(() => {
    const handleForceLogout = (e) => {
      const { code, message } = e?.detail || {};
      api.logout();
      setUser(null);
      localStorage.removeItem('loginTime');
      setShowLogin(true);
      setCurrentView('login');
      setAuthNotice(message || 'Your account has been deactivated or suspended. Please contact support.');
    };

    window.addEventListener('auth:force-logout', handleForceLogout);
    return () => window.removeEventListener('auth:force-logout', handleForceLogout);
  }, []);

  const [packages, setPackages] = useState([]);

  // Real-time unread notifications and floating toast state
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [toastNotification, setToastNotification] = useState(null);
  const seenNotifIdsRef = useRef(new Set());

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    let isMounted = true;
    let eventSource = null;

    const checkNotifications = async () => {
      try {
        const res = await fetch(`${API_BASE}/notifications?page=1&limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (isMounted && data.success && data.notifications) {
          if (data.unreadCount !== undefined) {
            setUnreadNotificationCount(data.unreadCount);
          }

          // Trigger toast for new unread notifications
          const unreadNotifs = data.notifications.filter(n => !n.isRead && n.status !== 'ARCHIVED');
          if (unreadNotifs.length > 0) {
            const newest = unreadNotifs[0];
            if (!seenNotifIdsRef.current.has(newest.id)) {
              seenNotifIdsRef.current.add(newest.id);
              setToastNotification(newest);
            }
          }
        }
      } catch (_) {}
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 15000);

    // Setup SSE stream for instant real-time push
    try {
      eventSource = new EventSource(`${API_BASE}/notifications/stream?token=${token}`);
      eventSource.onmessage = (event) => {
        try {
          const notif = JSON.parse(event.data);
          if (isMounted && notif && notif.id) {
            setUnreadNotificationCount(prev => prev + 1);
            if (!seenNotifIdsRef.current.has(notif.id)) {
              seenNotifIdsRef.current.add(notif.id);
              setToastNotification(notif);
            }
          }
        } catch (_) {}
      };
    } catch (_) {}

    return () => {
      isMounted = false;
      clearInterval(interval);
      if (eventSource) eventSource.close();
    };
  }, [user]);

  // Auto-dismiss floating toast after 7 seconds
  useEffect(() => {
    if (!toastNotification) return;
    const timer = setTimeout(() => {
      setToastNotification(null);
    }, 7000);
    return () => clearTimeout(timer);
  }, [toastNotification]);
  useEffect(() => {
    api.getPublicPackages().then(res => {
      if (res && res.packages) {
        setPackages(res.packages);
      }
    }).catch(e => console.error('Failed to load packages in App:', e));

    // Silently sync user tier and role from database status if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      api.getStatus().then(statusRes => {
        if (statusRes && statusRes.profile) {
          const freshTier = statusRes.profile.tier;
          const freshRole = statusRes.profile.role;
          const freshFullName = statusRes.profile.fullName;
          const freshAuthProvider = statusRes.profile.authProvider;
          const freshEmail = statusRes.profile.email;
          setUser(prev => {
            if (!prev) return prev;
            const freshMustChange = statusRes.profile.mustChangePassword;
            const needsUpdate = 
              prev.packageTier !== freshTier ||
              (freshRole && prev.role !== freshRole) ||
              (freshFullName !== undefined && prev.fullName !== freshFullName) ||
              (freshAuthProvider && prev.authProvider !== freshAuthProvider) ||
              (freshEmail && prev.email !== freshEmail) ||
              (freshMustChange !== undefined && prev.mustChangePassword !== freshMustChange);

            if (needsUpdate) {
              const updated = {
                ...prev,
                packageTier: freshTier || prev.packageTier,
                role: freshRole || prev.role,
                fullName: freshFullName !== undefined ? freshFullName : prev.fullName,
                authProvider: freshAuthProvider || prev.authProvider,
                email: freshEmail || prev.email,
                mustChangePassword: freshMustChange !== undefined ? Boolean(freshMustChange) : prev.mustChangePassword
              };
              const userStr = localStorage.getItem('user');
              if (userStr) {
                try {
                  const storedObj = JSON.parse(userStr);
                  storedObj.packageTier = freshTier;
                  if (freshRole) storedObj.role = freshRole;
                  if (freshFullName !== undefined) storedObj.fullName = freshFullName;
                  if (freshAuthProvider) storedObj.authProvider = freshAuthProvider;
                  if (freshEmail) storedObj.email = freshEmail;
                  if (freshMustChange !== undefined) storedObj.mustChangePassword = Boolean(freshMustChange);
                  localStorage.setItem('user', JSON.stringify(storedObj));
                } catch (_) {}
              }
              return updated;
            }
            return prev;
          });
        }
      }).catch(() => {});
    }
  }, []);

  const getPackageName = () => {
    if (!user) return '';
    const activePkg = packages.find(p => p.tierId === user.packageTier || p.id === user.packageTier);
    if (activePkg) return activePkg.tier.toUpperCase();
    if (user.packageTier === 'pro') return 'PRO AGENCY';
    if (user.packageTier === 'hobby') return 'HOBBY';
    if (user.packageTier === 'enterprise') return 'ENTERPRISE';
    return (user.packageTier || user.role).toUpperCase();
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const verifyToken = urlParams.get('verify_token');

    if (verifyToken) {
      const verifyEmailProcess = async () => {
        try {
          await api.verifyEmail(verifyToken);
          showConfirm(
            'Email Verified!',
            'Your email address has been successfully verified. You can now log in.',
            () => {
              window.history.replaceState({}, document.title, window.location.pathname);
              setCurrentView('login');
              setShowLogin(true);
              setInitialSignUpMode(false);
            },
            'success',
            'Go to Login'
          );
        } catch (err) {
          showConfirm(
            'Verification Failed',
            err.message || 'The verification link is invalid or has expired.',
            () => {
              window.history.replaceState({}, document.title, window.location.pathname);
            },
            'error',
            'Close'
          );
        }
      };
      verifyEmailProcess();
    } else if (user && sessionId) {
      // Returning from Stripe Checkout — verify payment and refresh user role
      const verifyAndRefresh = async () => {
        try {
          const token = localStorage.getItem('token');
          const verifyRes = await fetch(`${API_BASE}/stripe/verify-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ sessionId })
          });
          const verifyData = await verifyRes.json();

          if (verifyData.success && verifyData.token) {
            // Update session with the new role and token from the backend
            api.setSession(verifyData);
            setUser({
              username: verifyData.username,
              role: verifyData.role,
              apiKey: verifyData.apiKey,
              isOwner: verifyData.isOwner,
              packageTier: verifyData.packageTier
            });
          }
        } catch (err) {
          console.error('Failed to verify Stripe session:', err);
        }
        // Clean up URL and navigate to billing
        window.history.replaceState({}, document.title, window.location.pathname);
        setCurrentView('billing');
      };
      verifyAndRefresh();
    } else if (!user && !showLogin) {
      setCurrentView('landing');
    } else if (!user && showLogin) {
      setCurrentView('login');
    }

    // Set login time when user is loaded from storage or fresh login
    if (user && !localStorage.getItem('loginTime')) {
      const now = new Date().toLocaleTimeString();
      setLoginTime(now);
      localStorage.setItem('loginTime', now);
    } else if (localStorage.getItem('loginTime')) {
      setLoginTime(localStorage.getItem('loginTime'));
    }

    // Apply saved display settings on mount
    let savedFontSize = localStorage.getItem('fontSize') || 'small';
    let savedUiScale = localStorage.getItem('uiScale') || 'compact';
    let savedTheme = localStorage.getItem('theme') || 'light'; // Default to light

    try {
      const rawUser = localStorage.getItem('user');
      const parsedUser = rawUser ? JSON.parse(rawUser) : null;
      if (parsedUser && parsedUser.username) {
        const raw = localStorage.getItem(`settings:${parsedUser.username}`);
        if (raw) {
          const s = JSON.parse(raw);
          savedFontSize = s.fontSize || savedFontSize;
          savedUiScale = s.uiScale || savedUiScale;
          savedTheme = s.theme || savedTheme;
        }
      }
    } catch (e) {
      console.error('Failed to load settings:', e);
    }

    document.documentElement.setAttribute('data-font-size', savedFontSize);
    document.documentElement.setAttribute('data-ui-scale', savedUiScale);
    document.documentElement.setAttribute('data-theme', savedTheme);

    // Loading splash: fade out after 1.4s, unmount at 1.9s
    const fadeTimer = setTimeout(() => setFadeOut(true), 1400);
    const doneTimer = setTimeout(() => setLoading(false), 1900);
    return () => { clearTimeout(fadeTimer); clearTimeout(doneTimer); };
  }, [user, showLogin]);

  const handleLogin = (userData, isNewUser = false) => {
    const now = new Date().toLocaleTimeString();
    setUser(userData);
    setLoginTime(now);
    localStorage.setItem('loginTime', now);
    api.user = userData;
    setShowLogin(false);

    const hasPreAssignedPlan = Boolean(
      userData.packageTier === 'custom' ||
      userData.customLimits ||
      userData.createdBy ||
      (userData.packageTier && userData.packageTier !== 'hobby' && userData.subscriptionStatus === 'active')
    );

    if (userData.role === 'superadmin') {
      navigate('/superadmin');
    } else if (isNewUser && !hasPreAssignedPlan) {
      setCurrentView('onboarding');
      navigate('/');
    } else {
      setCurrentView('dashboard');
      navigate('/');
    }
  };

  const handleLogout = () => {
    showConfirm(
      'Confirm Logout',
      'Are you sure you want to log out? Any unsaved work in the code editor will be lost.',
      () => {
        api.logout();
        setUser(null);
        localStorage.removeItem('loginTime');
        setShowLogin(false);
        setCurrentView('landing');
        navigate('/');
      },
      'warning',
      'Logout'
    );
  };

  const handleStatusMessage = (message, type = 'info') => {
    // Avoid re-setting the same message/type to prevent UI flicker/glitches
    // If same message + type already visible, ignore
    if (status && status.message === message && status.type === type && statusVisible) return;

    // Clear any existing timers to avoid overlap
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current);
      statusTimerRef.current = null;
    }
    if (statusFadeTimerRef.current) {
      clearTimeout(statusFadeTimerRef.current);
      statusFadeTimerRef.current = null;
    }

    // Show status and ensure it's visible for the desired duration
    setStatusVisible(false);
    setStatus({ message, type });

    setTimeout(() => {
      setStatusVisible(true);
    }, 10);

    // Calculate duration: success messages are short, others last longer
    const duration = type === 'success' ? 700 : 5000;

    statusTimerRef.current = setTimeout(() => {
      setStatusVisible(false);
      statusTimerRef.current = null;

      statusFadeTimerRef.current = setTimeout(() => {
        setStatus(null);
        statusFadeTimerRef.current = null;
      }, 300);
    }, duration);
  };

  useEffect(() => {
    return () => {
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current);
        statusTimerRef.current = null;
      }
      if (statusFadeTimerRef.current) {
        clearTimeout(statusFadeTimerRef.current);
        statusFadeTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleSwitchToDashboard = () => handleViewChange('dashboard');
    window.addEventListener('switch-to-dashboard', handleSwitchToDashboard);
    return () => window.removeEventListener('switch-to-dashboard', handleSwitchToDashboard);
  }, []);

  if (loading) {
    return (
      <div className={`app-loading-screen${fadeOut ? ' fade-out' : ''}`} style={{ 
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
          height: '100vh', width: '100vw', gap: '1.5rem' 
      }}>
        <div style={{ position: 'relative', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', inset: 0, border: '3px solid transparent', 
            borderTopColor: 'var(--primary)', borderRightColor: 'var(--primary)', 
            borderRadius: '50%', animation: 'spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
            opacity: 0.8
          }} />
          <div style={{
            position: 'absolute', inset: '10px', border: '3px solid transparent', 
            borderBottomColor: 'var(--primary)', borderLeftColor: 'var(--primary)', 
            borderRadius: '50%', animation: 'spin 0.8s linear infinite reverse',
            opacity: 0.4
          }} />
          <Cloud size={32} style={{ color: 'var(--primary)', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite', zIndex: 2 }} />
        </div>
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(0.9); opacity: 0.7; } }
        `}</style>
      </div>
    );
  }

  if (currentView === 'landing' && window.location.pathname !== '/devorants-master-key' && !window.location.pathname.startsWith('/superadmin')) {
    return <Landing user={user} onLoginClick={(planId = 'hobby', isSignUp = false) => { localStorage.setItem('selectedPlan', planId); setInitialSignUpMode(isSignUp); setShowLogin(true); setCurrentView('login'); }} onDashboardClick={() => setCurrentView('dashboard')} />;
  }

  // If superadmin accesses root directly, redirect them to /superadmin
  if (user && user.role === 'superadmin' && window.location.pathname === '/') {
    return <Navigate to="/superadmin" replace />;
  }

  // Handle standard login vs superadmin login routing
  if (!user && (window.location.pathname.startsWith('/superadmin') || window.location.pathname === '/devorants-master-key')) {
    // Note: We render SuperAdminLogin inside the Routes block below,
    // so we just return null here or let it fall through to mainLayout.
    // However, since currentView is often 'dashboard' or 'landing', let's let it fall through
    // to the Routes block so react-router handles it.
  } else if (!user && currentView !== 'landing') {
    return (
      <Login
        onLogin={(userData, isNew) => {
          setAuthNotice(null);
          handleLogin(userData, isNew);
        }}
        initialIsSignUp={initialSignUpMode}
        initialError={authNotice}
        onBack={() => { setShowLogin(false); setCurrentView('landing'); setAuthNotice(null); }}
      />
    );
  }

  if (currentView === 'onboarding') {
    return <Onboarding onComplete={() => setCurrentView('dashboard')} />;
  }

  const mainLayout = (
    <div className="container">
      <main className="app-main-layout">
        <div className={appStyles.premiumLayoutWrapper}>
          {/* Mobile backdrop */}
          {mobileMenuOpen && (
            <div
              className="mobile-menu-backdrop"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
          )}

          {/* Always-visible mobile top bar */}
          <div className="mobile-topbar">
            <div className="sidebar-brand" onClick={() => { handleViewChange('dashboard'); setMobileMenuOpen(false); }} style={{ cursor: 'pointer' }}>
              <div className="app-brand-mark">
                <Cloud size={20} strokeWidth={2.2} />
              </div>
              <div className="sidebar-brand-text">
                <h1 className="app-title">DEVOR<span className="title-accent">ANTS</span></h1>
                <div className="app-subtitle">Deploy &amp; monitor</div>
              </div>
            </div>
            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              style={{ opacity: mobileMenuOpen ? 0 : 1, pointerEvents: mobileMenuOpen ? 'none' : 'auto' }}
            >
              <Menu size={22} />
            </button>
          </div>

          {/* Sidebar — desktop: always visible left panel; mobile: slide-in drawer */}
          <aside className={`app-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {/* Sidebar brand (desktop only, hidden on mobile since mobile-topbar handles it) */}
            <div className="sidebar-brand-wrapper">
              <div className="sidebar-brand" onClick={() => handleViewChange('dashboard')}>
                <div className="app-brand-mark">
                  <Cloud size={20} strokeWidth={2.2} />
                </div>
                <div className="sidebar-brand-text">
                  <h1 className="app-title">DEVOR<span className="title-accent">ANTS</span></h1>
                  <div className="app-subtitle">Deploy &amp; monitor</div>
                </div>
              </div>
              {/* Mobile close button inside the drawer */}
              <button
                className="mobile-drawer-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            {/* Nav */}
            <div className="sidebar-nav-area">
              <Navigation
                currentView={currentView}
                onViewChange={(viewId) => {
                  handleViewChange(viewId);
                  setMobileMenuOpen(false);
                }}
                user={user}
                packages={packages}
                unreadCount={unreadNotificationCount}
              />
            </div>

          </aside>

          <section className="app-content-area">
            <header className="app-header">
              <div className="app-header-left">
                <div className="breadcrumb-nav">
                  {currentView.startsWith('deploy-') ? (
                    <>
                      <span className="breadcrumb-root" onClick={() => handleViewChange('deploy')} style={{ cursor: 'pointer' }}>Deploy App</span>
                      <span className="breadcrumb-divider"><ChevronRight size={14} /></span>
                      <span className="breadcrumb-active">
                        {currentView.startsWith('deploy-hub') && 'Docker Hub Deployment'}
                        {currentView.startsWith('deploy-repo') && 'Git Repository Deployment'}
                        {currentView.startsWith('deploy-zip') && 'ZIP Package Deployment'}
                        {currentView.startsWith('deploy-docker') && 'Docker Tarball Deployment'}
                        {currentView.startsWith('deploy-compose') && 'Docker Compose Workspace'}
                        {currentView.startsWith('deploy-template') && 'Template Stack Deployment'}
                        {currentView.startsWith('deploy-ai-deploy') && 'AI Auto-Deploy'}
                        {currentView.startsWith('deploy-webhooks') && 'API & Webhooks Deployment'}
                      </span>
                      {currentView.includes('-configure') && (
                        <>
                          <span className="breadcrumb-divider"><ChevronRight size={14} /></span>
                          <span className="breadcrumb-active">Configure</span>
                        </>
                      )}
                      {currentView.includes('-deploying') && (
                        <>
                          <span className="breadcrumb-divider"><ChevronRight size={14} /></span>
                          <span className="breadcrumb-active">Deploying</span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="breadcrumb-active">
                      {currentView === 'admin' ? 'System Administration' : currentView === 'auto-deploy' ? 'Auto Deployment' : currentView.replace('-', ' ')}
                    </span>
                  )}
                </div>
              </div>
              <div className="app-header-right">
                <div className="header-meta-group">
                  <div className="header-meta-tag login-time-pill">
                    <History size={13} strokeWidth={2.5} />
                    <span>Logged in: {loginTime}</span>
                  </div>

                  <div className="premium-user-widget">
                    <div className="puw-avatar">
                      <UserRound size={15} strokeWidth={2.5} />
                    </div>
                    <div className="puw-details">
                      <span className="puw-name">{user?.username || 'User'}</span>
                      <span className="puw-role">{getPackageName()}</span>
                    </div>
                    <button className="puw-logout" onClick={handleLogout} title="Logout">
                      <LogOut size={15} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {status && (
              <div className={`status-msg ${status.type} ${statusVisible ? 'show' : 'hide'}`}>
                {status.message}
              </div>
            )}

            <div className="view-section active" style={{ display: (currentView === 'dashboard' || currentView === 'stats') ? 'block' : 'none' }}>
              <Dashboard
                key={`dashboard-${viewKeys[currentView === 'stats' ? 'stats' : 'dashboard']}`}
                user={user}
                onStatusMessage={handleStatusMessage}
                onConfirm={showConfirm}
                activeTab={currentView === 'stats' ? 'stats' : 'deployments'}
                onDeployRequested={(tab) => handleViewChange(tab ? `deploy-${tab}` : 'deploy')}
                openGlobalWorkspace={(ws) => {
                  setGlobalWorkspace(ws);
                  setGlobalWorkspaceMinimized(false);
                }}
              />
            </div>

            {(user?.role === 'developer' || user?.role === 'admin') && (
              <div className="view-section" style={{ display: (currentView === 'deploy' || currentView === 'auto-deploy' || currentView.startsWith('deploy-')) ? 'block' : 'none' }}>
                <Deploy
                  key={`deploy-${viewKeys.deploy}`}
                  user={user}
                  onStatusMessage={handleStatusMessage}
                  onConfirm={showConfirm}
                  initialTab={(currentView === 'auto-deploy' || currentView === 'deploy-ai-deploy') ? 'ai-deploy' : (currentView.startsWith('deploy-') ? currentView.replace('deploy-', '') : null)}
                  onTabChange={(tab) => {
                    // Only sync if we are already in some kind of deploy view
                    const isDeployView = currentView === 'deploy' || currentView === 'auto-deploy' || currentView.startsWith('deploy-');
                    if (!isDeployView) return;

                    if (tab === 'deployments' || tab === 'dashboard') {
                      handleViewChange('dashboard');
                      return;
                    }

                    if (tab === 'auto-deploy' || tab === 'ai-deploy') {
                      handleViewChange('deploy-ai-deploy');
                      return;
                    }

                    if (tab) {
                      const newView = `deploy-${tab}`;
                      if (currentView !== newView) handleViewChange(newView);
                    } else {
                      if (currentView !== 'deploy') handleViewChange('deploy');
                    }
                  }}
                  openGlobalWorkspace={(ws) => {
                    setGlobalWorkspace(ws);
                    setGlobalWorkspaceMinimized(false);
                  }}
                />
              </div>
            )}

            <div className="view-section" style={{ display: currentView === 'activity' ? 'block' : 'none' }}>
              <Activity
                key={`activity-${viewKeys.activity}`}
                user={user}
              />
            </div>

            {(user?.role === 'admin' || user?.isOwner) && (
              <div className="view-section" style={{ display: currentView === 'admin' ? 'block' : 'none' }}>
                <Admin
                  key={`admin-${viewKeys.admin || 0}`}
                  user={user}
                  onStatusMessage={handleStatusMessage}
                />
              </div>
            )}

            <div className="view-section" style={{ display: currentView === 'settings' ? 'block' : 'none' }}>
              <Settings
                key={`settings-${viewKeys.settings}`}
                user={user}
                onStatusMessage={handleStatusMessage}
              />
            </div>

            {(user?.role === 'developer' || user?.role === 'admin') && (
              <div className="view-section" style={{ display: currentView === 'backups' ? 'block' : 'none' }}>
                <Backups
                  key={`backups-${viewKeys.backups}`}
                />
              </div>
            )}

            <div className="view-section" style={{ display: currentView === 'profile' ? 'block' : 'none' }}>
              <Profile user={user} onStatusMessage={handleStatusMessage} onUserUpdate={(updated) => setUser(prev => ({ ...prev, ...updated }))} />
            </div>
            <div className="view-section" style={{ display: currentView === 'billing' ? 'block' : 'none' }}>
              <Billing 
                onUpgradeClick={() => handleViewChange('plans')} 
                onUserUpdate={(updatedUserData) => {
                  if (updatedUserData) {
                    setUser(prev => ({
                      ...prev,
                      role: updatedUserData.role || prev?.role,
                      packageTier: updatedUserData.packageTier || prev?.packageTier
                    }));
                  }
                }}
              />
            </div>
            <div className="view-section" style={{ display: currentView === 'plans' ? 'block' : 'none' }}>
              <Plans onBack={() => handleViewChange('billing')} />
            </div>
            <div className="view-section" style={{ display: currentView === 'notifications' ? 'block' : 'none' }}>
              <Notifications 
                key={`notifications-${viewKeys.notifications || 0}`}
                onNavigate={handleViewChange} 
                onUnreadCountChange={(cnt) => setUnreadNotificationCount(cnt)} 
              />
            </div>
            <div className="view-section" style={{ display: currentView === 'integrations' ? 'block' : 'none' }}>
              <Integrations />
            </div>
            <div className="view-section" style={{ display: currentView === 'support' ? 'block' : 'none' }}>
              <Support />
            </div>
          </section>
        </div>
      </main>
      {/* Global Custom Confirm Dialog */}
      {confirmDialog.visible && (
        <div className="custom-modal-overlay" onClick={() => setConfirmDialog(prev => ({ ...prev, visible: false }))}>
          <div className="custom-modal-content" onClick={e => e.stopPropagation()}>
            <div className="custom-modal-header">
              <div className={`custom-modal-icon ${confirmDialog.type}`}>
                {confirmDialog.type === 'danger' && <Trash2 size={24} />}
                {confirmDialog.type === 'warning' && <AlertTriangle size={24} />}
                {confirmDialog.type === 'info' && <Info size={24} />}
              </div>
              <h3 className="custom-modal-title">{confirmDialog.title}</h3>
            </div>
            <div className="custom-modal-message">{confirmDialog.message}</div>
            <div className="custom-modal-actions">
              <button className="custom-modal-btn cancel" onClick={() => setConfirmDialog(prev => ({ ...prev, visible: false }))}>Cancel</button>
              <button className={`custom-modal-btn confirm ${confirmDialog.type === 'danger' ? 'danger' : ''}`} onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog(prev => ({ ...prev, visible: false }));
              }}>
                {confirmDialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {user && user.mustChangePassword && (
        <FirstLoginPasswordModal
          user={user}
          onPasswordChanged={(updatedData) => {
            setUser(prev => ({ ...prev, ...updatedData, mustChangePassword: false }));
            if (api.user) api.user.mustChangePassword = false;
            handleStatusMessage('Your password has been successfully set. Welcome to Devorants!', 'success');
          }}
        />
      )}

      {globalWorkspace && (
        <ComposeWorkspaceModal
          appName={globalWorkspace.appName}
          owner={user?.username}
          sshInfo={globalWorkspace.sshInfo}
          deploymentSession={globalWorkspace.deploymentSession}
          deploymentType={globalWorkspace.deploymentType || 'compose'}
          initialTab={globalWorkspace.initialTab || 'ai'}
          autoDeploy={globalWorkspace.autoDeploy === true}
          isMinimized={globalWorkspaceMinimized}
          onClose={(options = {}) => {
            setGlobalWorkspace(null);
            // After verified AI deploy, keep the user on the Deploy success page.
            if (options?.verified) return;
          }}
          onCancel={async () => {
            if (globalWorkspace.deploymentSession?.id || globalWorkspace.deploymentSessionId) {
              try {
                await api.cancelDeployment(globalWorkspace.deploymentSession?.id || globalWorkspace.deploymentSessionId);
              } catch (e) {
                // handleStatusMessage not in scope? wait, there is no handleStatusMessage here.
                // Oh wait, there is no handleStatusMessage. I should use console.error for now, or just let it fail silently.
                console.error("Cancel failed:", e);
              }
            }
            setGlobalWorkspace(null);
          }}
          onMinimize={() => setGlobalWorkspaceMinimized(true)}
          onRestore={() => setGlobalWorkspaceMinimized(false)}
        />
      )}

      {/* Floating Toast Notification */}
      {toastNotification && (
        <div 
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 999999,
            maxWidth: '410px',
            width: 'calc(100vw - 48px)',
            background: 'linear-gradient(145deg, #131620 0%, #0d0f15 100%)',
            border: '1px solid rgba(99, 102, 241, 0.35)',
            borderRadius: '16px',
            boxShadow: '0 20px 48px rgba(0, 0, 0, 0.75), 0 0 24px rgba(99, 102, 241, 0.2)',
            padding: '1.1rem 1.2rem',
            backdropFilter: 'blur(20px)',
            animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            display: 'flex',
            gap: '0.9rem',
            alignItems: 'flex-start'
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.15) 100%)',
            border: '1px solid rgba(99, 102, 241, 0.4)',
            color: '#818cf8',
            padding: '0.6rem',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 0 16px rgba(99, 102, 241, 0.25)'
          }}>
            <Bell size={20} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#6366f1',
                  boxShadow: '0 0 8px #6366f1'
                }} />
                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#ffffff', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                  {toastNotification.title || 'New Notification'}
                </h4>
              </div>
              <button 
                onClick={() => setToastNotification(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'; }}
                title="Dismiss"
              >
                <X size={15} />
              </button>
            </div>

            <p style={{
              margin: '0.5rem 0 0.85rem 0',
              fontSize: '0.85rem',
              color: '#f1f5f9',
              fontWeight: 450,
              lineHeight: 1.55,
              wordBreak: 'break-word',
              letterSpacing: '0.01em'
            }}>
              {(() => {
                const msg = toastNotification.message || '';
                if (msg.includes('custom_')) {
                  const cleanPlan = getPackageName() ? `${getPackageName()} Plan` : 'Pro Plan';
                  return msg.replace(/\(custom_[^)]+\)/gi, `(${cleanPlan})`).replace(/custom_\w+/gi, cleanPlan);
                }
                return msg;
              })()}
            </p>

            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
              {toastNotification.link && (
                <button
                  onClick={() => {
                    const targetView = toastNotification.link.replace(/^\//, '');
                    handleViewChange(targetView);
                    setToastNotification(null);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '0.42rem 0.85rem',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(99, 102, 241, 0.6)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.4)'; }}
                >
                  {toastNotification.link.includes('billing') || toastNotification.link.includes('plan') ? (
                    <>Upgrade Plan <ExternalLink size={13} /></>
                  ) : toastNotification.link.includes('support') || toastNotification.link.includes('ticket') ? (
                    <>View Ticket <ExternalLink size={13} /></>
                  ) : (
                    <>Open <ExternalLink size={13} /></>
                  )}
                </button>
              )}
              <button
                onClick={() => {
                  handleViewChange('notifications');
                  setToastNotification(null);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.07)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  borderRadius: '8px',
                  padding: '0.42rem 0.85rem',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.07)'; }}
              >
                View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Routes>
      <Route path="/devorants-master-key" element={
        user && user.role === 'superadmin' ? (
          <Navigate to="/superadmin" replace />
        ) : (
          <SuperAdminLogin
            onLogin={(userData) => {
              handleLogin(userData, false);
              navigate('/superadmin');
            }}
            onBack={() => {
              setCurrentView('landing');
              navigate('/');
            }}
          />
        )
      } />
      <Route path="/superadmin/*" element={
        user && user.role === 'superadmin' ? (
          <SuperAdminDashboard
            user={user}
            onLogout={() => {
              api.logout();
              setUser(null);
              localStorage.removeItem('loginTime');
              setShowLogin(false);
              setCurrentView('landing');
              navigate('/');
            }}
          />
        ) : (
          <Navigate to="/devorants-master-key" replace />
        )
      } />
      <Route path="/*" element={mainLayout} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
